import { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, LabelList, ReferenceLine,
} from "recharts";
import {
  BarChart3, Users2, Clock3, TrendingUp, PhoneOff, MessageCircle, X, Plus,
} from "lucide-react";
import { toast } from "sonner";
import {
  getAnalyticsRefusalByCod,
  getAnalyticsRefusalByTransit,
  getAnalyticsNewVsRecurring,
  getAnalyticsWeeklyTrend,
  getRepeatRefusers,
  addTrendAnnotation,
  deleteTrendAnnotation,
} from "@/lib/delivery";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "2-digit" });
}
function fmtWeek(iso) {
  const d = new Date(iso);
  const w = Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
  return `S${w} ${String(d.getFullYear()).slice(2)}`;
}
function fmtMoney(n) {
  return `${Number(n || 0).toFixed(0)} lei`;
}

export default function AnalyticsTab() {
  const [days, setDays] = useState(90);
  const [weeks, setWeeks] = useState(26);
  const [cod, setCod] = useState([]);
  const [transit, setTransit] = useState([]);
  const [nvr, setNvr] = useState(null);
  const [trend, setTrend] = useState({ series: [], annotations: [] });
  const [refusers, setRefusers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [annOpen, setAnnOpen] = useState(false);
  const [annPoint, setAnnPoint] = useState(null); // { week_start }
  const [annLabel, setAnnLabel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, t, n, tr, r] = await Promise.all([
        getAnalyticsRefusalByCod(days),
        getAnalyticsRefusalByTransit(days),
        getAnalyticsNewVsRecurring(days),
        getAnalyticsWeeklyTrend(weeks),
        getRepeatRefusers(days, 20),
      ]);
      setCod(c);
      setTransit(t);
      setNvr(n);
      setTrend(tr);
      setRefusers(r);
    } catch (e) {
      toast.error("Eroare la încărcarea analizei: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days, weeks]);

  const nvrData = useMemo(() => {
    if (!nvr) return [];
    return [
      { label: "Client nou", ...nvr.new },
      { label: "Client recurent", ...nvr.recurring },
    ];
  }, [nvr]);

  const weeklyData = useMemo(() => {
    return trend.series.map((s) => ({ ...s, week_label: fmtWeek(s.week_start) }));
  }, [trend.series]);

  const saveAnnotation = async () => {
    if (!annPoint?.week_start || !annLabel.trim()) return;
    try {
      const created = await addTrendAnnotation(annPoint.week_start, annLabel.trim());
      setTrend((t) => ({ ...t, annotations: [...t.annotations, created] }));
      setAnnOpen(false); setAnnLabel(""); setAnnPoint(null);
      toast.success("Adnotare adăugată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const removeAnnotation = async (id) => {
    try {
      await deleteTrendAnnotation(id);
      setTrend((t) => ({ ...t, annotations: t.annotations.filter((a) => a.id !== id) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {/* Period selector */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Perioadă:</span>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-[130px] h-9" data-testid="analytics-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Ultimele 30 zile</SelectItem>
              <SelectItem value="90">Ultimele 90 zile</SelectItem>
              <SelectItem value="180">Ultimele 180 zile</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Trend:</span>
          <Select value={String(weeks)} onValueChange={(v) => setWeeks(parseInt(v))}>
            <SelectTrigger className="w-[130px] h-9" data-testid="analytics-weeks"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="13">13 săptămâni</SelectItem>
              <SelectItem value="26">26 săptămâni</SelectItem>
              <SelectItem value="52">52 săptămâni</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {loading && <span className="text-xs text-muted-foreground">Se încarcă...</span>}
      </div>

      {/* Row 1: COD tier + Transit time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" /> Rată refuz pe tranșe valoare COD
            </CardTitle>
          </CardHeader>
          <CardContent className="h-[260px]">
            <ResponsiveContainer>
              <BarChart data={cod} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="rate" tick={{ fontSize: 11 }} unit="%" domain={[0, "dataMax + 2"]} />
                <YAxis yAxisId="cnt" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => n === "refusal_rate" ? `${v}%` : v} />
                <Bar yAxisId="cnt" dataKey="total" fill="#93c5fd" name="Colete" />
                <Bar yAxisId="rate" dataKey="refusal_rate" fill="#f43f5e" name="% refuz">
                  <LabelList dataKey="refusal_rate" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 10, fill: "#be123c" }} />
                </Bar>
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-primary" /> Rată refuz vs timp tranzit
            </CardTitle>
          </CardHeader>
          <CardContent className="h-[260px]">
            <ResponsiveContainer>
              <BarChart data={transit} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="rate" tick={{ fontSize: 11 }} unit="%" domain={[0, "dataMax + 2"]} />
                <YAxis yAxisId="cnt" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => n === "refusal_rate" ? `${v}%` : v} />
                <Bar yAxisId="cnt" dataKey="total" fill="#a7f3d0" name="Colete" />
                <Bar yAxisId="rate" dataKey="refusal_rate" fill="#f97316" name="% refuz">
                  <LabelList dataKey="refusal_rate" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 10, fill: "#c2410c" }} />
                </Bar>
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Client nou vs recurent */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users2 className="h-4 w-4 text-primary" /> Client nou vs recurent (rată refuz)
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[200px]">
          <ResponsiveContainer>
            <BarChart data={nvrData} layout="vertical" margin={{ top: 10, right: 30, left: 90, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} unit="%" />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="refusal_rate" fill="#8b5cf6" name="% refuz">
                <LabelList dataKey="refusal_rate" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-xs text-muted-foreground mt-1 pl-24">
            Client recurent = ≥1 livrare reușită anterior pe același telefon
          </div>
        </CardContent>
      </Card>

      {/* Row 3: Weekly trend with annotations */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" /> Trend săptămânal ({weeks} săptămâni)
          </CardTitle>
          <div className="text-[11px] text-muted-foreground">Click pe punct pentru adnotare</div>
        </CardHeader>
        <CardContent className="h-[300px]">
          <ResponsiveContainer>
            <LineChart
              data={weeklyData}
              margin={{ top: 10, right: 12, left: 0, bottom: 4 }}
              onClick={(e) => {
                if (e?.activePayload?.[0]) {
                  setAnnPoint(e.activePayload[0].payload);
                  setAnnOpen(true);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="week_label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="delivery_rate" stroke="#10b981" strokeWidth={2} name="Rată livrare" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="refusal_rate" stroke="#f43f5e" strokeWidth={2} name="Rată refuz" dot={{ r: 3 }} />
              {trend.annotations.map((a) => {
                const wl = fmtWeek(a.at);
                return <ReferenceLine key={a.id} x={wl} stroke="#7c3aed" strokeDasharray="4 3" label={{ value: a.label, position: "top", fontSize: 10, fill: "#6d28d9" }} />;
              })}
            </LineChart>
          </ResponsiveContainer>
          {trend.annotations.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {trend.annotations.map((a) => (
                <Badge
                  key={a.id}
                  variant="outline"
                  className="text-[10px] gap-1 border-violet-300 bg-violet-50 text-violet-700"
                >
                  {fmtDate(a.at)} — {a.label}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => removeAnnotation(a.id)} />
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Row 4: Top 20 repeat refusers */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <PhoneOff className="h-4 w-4 text-primary" /> Top 20 telefoane cu refuzuri repetate ({days} zile)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="repeat-refusers-table">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">#</th>
                  <th className="text-left px-3 py-2 font-medium">Telefon</th>
                  <th className="text-left px-3 py-2 font-medium">Client</th>
                  <th className="text-center px-3 py-2 font-medium">Refuzuri</th>
                  <th className="text-right px-3 py-2 font-medium">Valoare refuzată</th>
                  <th className="text-left px-3 py-2 font-medium">Județe</th>
                  <th className="text-left px-3 py-2 font-medium">Ultimul refuz</th>
                </tr>
              </thead>
              <tbody>
                {refusers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-6 text-xs text-muted-foreground">
                      Niciun telefon cu 2+ refuzuri în perioada aleasă. (Vești bune!)
                    </td>
                  </tr>
                )}
                {refusers.map((r, i) => (
                  <tr key={r.phone} className="border-t hover:bg-muted/30">
                    <td className="px-3 py-2 text-muted-foreground text-xs">{i + 1}</td>
                    <td className="px-3 py-2 tabular-nums font-medium">{r.phone}</td>
                    <td className="px-3 py-2">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">
                        {r.refuse_count}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.total_refused_value)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{(r.counties || []).slice(0, 3).join(", ")}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(r.last_refuse_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Annotation dialog */}
      <Dialog open={annOpen} onOpenChange={setAnnOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adnotare săptămâna {annPoint ? fmtWeek(annPoint.week_start) : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              Adaugă un eveniment vizibil pe trendul de mai sus (ex: am introdus confirmarea telefonică).
            </div>
            <Input
              value={annLabel}
              onChange={(e) => setAnnLabel(e.target.value)}
              placeholder="Titlu adnotare"
              autoFocus
              data-testid="annotation-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnOpen(false)}>Anulează</Button>
            <Button onClick={saveAnnotation} disabled={!annLabel.trim()} data-testid="annotation-save-btn">
              <Plus className="h-4 w-4 mr-1" /> Adaugă
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
